import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { safeConsentReturnTo } from '../features/agent/oauthParams'

// «Запомнить меня»: если флаг выставлен — храним сессию в localStorage
// (переживает перезапуск браузера), иначе в sessionStorage (живёт до
// закрытия вкладки). Читаем всегда из обоих, чтобы api-клиент находил токен.
const REMEMBER_KEY = 'auth-remember'

const authStorage = {
  getItem: (name: string): string | null =>
    localStorage.getItem(name) ?? sessionStorage.getItem(name),
  setItem: (name: string, value: string): void => {
    const remember = localStorage.getItem(REMEMBER_KEY) !== 'false'
    if (remember) {
      localStorage.setItem(name, value)
      sessionStorage.removeItem(name)
    } else {
      sessionStorage.setItem(name, value)
      localStorage.removeItem(name)
    }
  },
  removeItem: (name: string): void => {
    localStorage.removeItem(name)
    sessionStorage.removeItem(name)
  },
}

export interface User {
  user_id: number
  user_name: string
  email: string
  role: string
  is_active: number
  permissions?: string[]
}

interface AuthState {
  isAuthenticated: boolean
  user: User | null
  token: string | null
  permissionsLoaded: boolean
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>
  logout: () => void
  setUser: (user: User) => void
  setToken: (token: string) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      user: null,
      token: null,
      permissionsLoaded: false,
      
      login: async (email: string, password: string, rememberMe: boolean = true) => {
        try {
          // Фиксируем выбор «Запомнить меня» до записи стора в хранилище
          localStorage.setItem(REMEMBER_KEY, rememberMe ? 'true' : 'false')

          // Используем ту же логику, что в api.ts
          const envApiUrl = import.meta.env.VITE_BACKEND_API_URL || 'http://localhost:3030';
          const API_BASE_URL = envApiUrl.startsWith('/') 
            ? envApiUrl  // Относительный путь уже содержит /api/v1
            : `${envApiUrl}/api/v1`;  // Абсолютный URL - добавляем /api/v1
          
          const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
          })
          
          if (!response.ok) {
            throw new Error('Login failed')
          }
          
          const data = await response.json()
          
          // Auth-service возвращает accessToken, а не token
          if (!data.data || !data.data.accessToken) {
            throw new Error('Invalid response format')
          }
          
          set({
            isAuthenticated: true,
            user: data.data.user,
            token: data.data.accessToken, // Используем accessToken вместо token
            permissionsLoaded: Array.isArray(data.data.user?.permissions),
          })
          
          // После OAuth-входа возвращаем только на локальный экран согласия.
          const returnTo = new URLSearchParams(window.location.search).get('returnTo')
          window.location.href = safeConsentReturnTo(returnTo) ?? '/'
        } catch (error) {
          console.error('Login error:', error)
          throw error
        }
      },
      
      logout: () => {
        set({
          isAuthenticated: false,
          user: null,
          token: null,
          permissionsLoaded: false,
        })
      },
      
      setUser: (user: User) => {
        set({ user, permissionsLoaded: Array.isArray(user.permissions) })
      },
      
      setToken: (token: string) => {
        set({ token })
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => authStorage),
      // Обработка ошибок при чтении из хранилища
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        // Permissions are always refreshed from /auth/me after rehydration so a
        // role change cannot leave stale frontend access until the next login.
        user: state.user ? { ...state.user, permissions: undefined } : null,
        token: state.token,
        permissionsLoaded: false,
      }),
      // Обработка ошибок при десериализации
      onRehydrateStorage: () => (_state, error) => {
        if (error) {
          console.error('Error rehydrating auth store:', error);
          // Очищаем поврежденные данные
          try {
            localStorage.removeItem('auth-storage');
            sessionStorage.removeItem('auth-storage');
          } catch (e) {
            console.error('Error clearing auth storage:', e);
          }
        }
      },
    }
  )
)
